import { Ionicons } from '@expo/vector-icons';
import { useEffect, useState } from 'react';
import {
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SuggestionHistoryItem } from '@/components/suggestions/SuggestionHistoryItem';
import { SuggestionDetailOverlay } from '@/components/suggestions/SuggestionDetailOverlay';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getBodyFont } from '@/constants/typography';
import type { AISuggestion, ArchivedSuggestion } from '@/types/ai-suggestions';

type SuggestionHistorySheetProps = {
  visible: boolean;
  archivedSuggestions: ArchivedSuggestion[];
  onClose: () => void;
  onAddToCalendar: (suggestion: AISuggestion) => void;
  onAddToTodo: (suggestion: AISuggestion) => Promise<boolean>;
};

/**
 * SuggestionHistorySheet - Slide-up modal showing archived suggestions.
 * Displays dismissed and added suggestions with action badges and timestamps.
 */
export function SuggestionHistorySheet({
  visible,
  archivedSuggestions,
  onClose,
  onAddToCalendar,
  onAddToTodo,
}: SuggestionHistorySheetProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();

  const [selectedSuggestion, setSelectedSuggestion] = useState<AISuggestion | null>(null);

  const translateY = useSharedValue(600);
  const backdropOpacity = useSharedValue(0);

  useEffect(() => {
    if (visible) {
      translateY.value = withTiming(0, { duration: 300 });
      backdropOpacity.value = withTiming(1, { duration: 300 });
    } else {
      translateY.value = withTiming(600, { duration: 250 });
      backdropOpacity.value = withTiming(0, { duration: 250 });
    }
  }, [visible, translateY, backdropOpacity]);

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const sheetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.modalContainer}>
        <Animated.View style={[styles.backdrop, backdropStyle]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        <Animated.View
          style={[
            styles.sheet,
            {
              backgroundColor: palette.cardBackground,
              paddingBottom: insets.bottom + Spacing.lg,
            },
            sheetStyle,
          ]}
        >
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
            <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
              History
            </Text>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={24} color={palette.text} />
            </TouchableOpacity>
          </View>

          {/* List */}
          {archivedSuggestions.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="time-outline" size={48} color={palette.textMuted} />
              <Text style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                No suggestion history yet
              </Text>
            </View>
          ) : (
            <FlatList
              data={archivedSuggestions}
              keyExtractor={(item) => `${item.suggestion.id}-${item.archivedAt}`}
              renderItem={({ item }) => (
                <SuggestionHistoryItem
                  archivedSuggestion={item}
                  onPress={() => setSelectedSuggestion(item.suggestion)}
                />
              )}
              showsVerticalScrollIndicator={false}
            />
          )}
        </Animated.View>
      </View>

      {/* Detail overlay for tapped history item */}
      <SuggestionDetailOverlay
        suggestion={selectedSuggestion}
        onClose={() => setSelectedSuggestion(null)}
        onAddToCalendar={onAddToCalendar}
        onAddToTodo={onAddToTodo}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  sheet: {
    maxHeight: '70%',
    borderTopLeftRadius: BorderRadius.xl,
    borderTopRightRadius: BorderRadius.xl,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.md,
  },
  emptyText: {
    fontSize: 15,
  },
});
